import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Globe, HelpCircle, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Interruptor } from '@/shared/ui/Interruptor';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type { DatosDePregunta, PreguntaFrecuente } from './contrato';
import { PestanasDelPortal } from './PestanasDelPortal';

const CLAVE = ['portal', 'preguntas'];

export function PaginaPreguntasFrecuentes() {
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [editando, setEditando] = useState<PreguntaFrecuente | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<PreguntaFrecuente | null>(null);

  const preguntas = useQuery({
    queryKey: CLAVE,
    queryFn: () => api.get<PreguntaFrecuente[]>('/portal/preguntas-frecuentes'),
  });
  const lista = preguntas.data ?? [];

  function refrescar() {
    void cliente.invalidateQueries({ queryKey: CLAVE });
  }

  function alFallar(causa: unknown, respaldo: string) {
    notificar.error(causa instanceof ErrorDeApi ? causa.message : respaldo);
  }

  const publicar = useMutation({
    mutationFn: (p: PreguntaFrecuente) =>
      api.put<PreguntaFrecuente>(`/portal/preguntas-frecuentes/${p.id}`, {
        ...datosDe(p),
        publicada: !p.publicada,
      }),
    onSuccess: refrescar,
    onError: (causa) => alFallar(causa, 'No se pudo cambiar la publicación'),
  });

  const mover = useMutation({
    mutationFn: (ids: number[]) =>
      api.put<PreguntaFrecuente[]>('/portal/preguntas-frecuentes/orden', { ids }),
    onSuccess: refrescar,
    onError: (causa) => alFallar(causa, 'No se pudo cambiar el orden'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/portal/preguntas-frecuentes/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Pregunta eliminada');
    },
    onError: (causa) => alFallar(causa, 'No se pudo eliminar'),
  });

  function moverA(indice: number, destino: number) {
    const ids = lista.map((p) => p.id);
    const [quitado] = ids.splice(indice, 1);
    if (quitado !== undefined) {
      ids.splice(destino, 0, quitado);
      mover.mutate(ids);
    }
  }

  const categorias = [...new Set(lista.map((p) => p.categoria).filter((c): c is string => !!c))];

  return (
    <>
      <EncabezadoDePagina
        icono={HelpCircle}
        color="esmeralda"
        titulo="Portal ciudadano"
        subtitulo="Las preguntas frecuentes de la ventanilla"
        acciones={
          puede('portal.crear') && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              title="Nueva pregunta"
              aria-label="Nueva pregunta frecuente"
              className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-5" aria-hidden />
            </button>
          )
        }
      />

      <PestanasDelPortal />

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Globe className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">Lo que se publica aquí lo ve cualquier ciudadano.</span>{' '}
          La ventanilla lo toma en unos minutos, sin desplegar nada. Una pregunta sin publicar sigue
          guardada, pero no se enseña.
        </p>
      </div>

      {preguntas.isPending ? (
        <Cargando titulo="Cargando preguntas" detalle="Obteniendo registros" alto="h-48" />
      ) : lista.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-armazon-300 bg-white p-10 text-center text-sm uppercase text-armazon-500">
          Todavía no hay preguntas. Cree la primera con el botón +.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <div className="divide-y divide-armazon-100">
            {lista.map((p, i) => (
              <div
                key={p.id}
                className="flex flex-wrap items-start gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60"
              >
                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => moverA(i, i - 1)}
                    disabled={i === 0 || mover.isPending || !puede('portal.editar')}
                    title="Subir"
                    aria-label={`Subir ${p.pregunta}`}
                    className="rounded p-0.5 text-armazon-400 hover:bg-armazon-100 hover:text-armazon-700 disabled:opacity-30"
                  >
                    <ArrowUp className="size-3.5" aria-hidden />
                  </button>
                  <span className="importe grid size-7 place-items-center rounded-lg bg-armazon-100 text-xs font-semibold text-armazon-600">
                    {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => moverA(i, i + 1)}
                    disabled={i === lista.length - 1 || mover.isPending || !puede('portal.editar')}
                    title="Bajar"
                    aria-label={`Bajar ${p.pregunta}`}
                    className="rounded p-0.5 text-armazon-400 hover:bg-armazon-100 hover:text-armazon-700 disabled:opacity-30"
                  >
                    <ArrowDown className="size-3.5" aria-hidden />
                  </button>
                </div>

                <div className="min-w-0 flex-1 basis-72">
                  {p.categoria && (
                    <span className="mb-1 inline-block rounded-full bg-marca-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-marca-700 ring-1 ring-marca-100">
                      {p.categoria}
                    </span>
                  )}
                  <p className="text-sm font-semibold text-armazon-900">{p.pregunta}</p>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-armazon-500">
                    {p.respuesta}
                  </p>
                </div>

                <div className="flex w-28 shrink-0 flex-col items-center gap-1">
                  <Interruptor
                    activo={p.publicada}
                    alCambiar={() => publicar.mutate(p)}
                    etiqueta={`Publicar o retirar ${p.pregunta}`}
                    deshabilitado={publicar.isPending || !puede('portal.editar')}
                  />
                  <span
                    className={`text-[10px] font-semibold uppercase ${p.publicada ? 'text-marca-700' : 'text-armazon-400'}`}
                  >
                    {p.publicada ? 'Publicada' : 'Sin publicar'}
                  </span>
                </div>

                <div className="flex w-16 shrink-0 items-center justify-end gap-1">
                  {puede('portal.editar') && (
                    <button
                      type="button"
                      onClick={() => setEditando(p)}
                      title="Editar"
                      aria-label={`Editar ${p.pregunta}`}
                      className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                    >
                      <Pencil className="size-[15px]" aria-hidden />
                    </button>
                  )}
                  {puede('portal.eliminar') && (
                    <button
                      type="button"
                      onClick={() => setAEliminar(p)}
                      title="Eliminar"
                      aria-label={`Eliminar ${p.pregunta}`}
                      className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                    >
                      <Trash2 className="size-[15px]" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogoDePregunta
        key={editando?.id ?? 'nueva'}
        abierto={creando || editando !== null}
        pregunta={editando}
        categorias={categorias}
        siguienteOrden={lista.length + 1}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => setAEliminar(null)}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar pregunta"
        descripcion="Deja de verse en la ventanilla y no se puede recuperar. Para retirarla sin perderla, quítele la publicación."
        detalle={aEliminar?.pregunta}
        error={null}
      />
    </>
  );
}

function datosDe(p: PreguntaFrecuente): DatosDePregunta {
  return {
    categoria: p.categoria,
    pregunta: p.pregunta,
    respuesta: p.respuesta,
    orden: p.orden,
    publicada: p.publicada,
  };
}

function DialogoDePregunta({
  abierto,
  pregunta,
  categorias,
  siguienteOrden,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  pregunta: PreguntaFrecuente | null;
  categorias: string[];
  siguienteOrden: number;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = pregunta !== null;
  const [categoria, setCategoria] = useState(pregunta?.categoria ?? '');
  const [texto, setTexto] = useState(pregunta?.pregunta ?? '');
  const [respuesta, setRespuesta] = useState(pregunta?.respuesta ?? '');
  const [publicada, setPublicada] = useState(pregunta?.publicada ?? true);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = texto.trim() !== '' && respuesta.trim() !== '';

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      const cuerpo: DatosDePregunta = {
        categoria: categoria.trim() || null,
        pregunta: texto.trim(),
        respuesta: respuesta.trim(),
        orden: pregunta?.orden ?? siguienteOrden,
        publicada,
      };
      if (editando) {
        await api.put<PreguntaFrecuente>(`/portal/preguntas-frecuentes/${pregunta.id}`, cuerpo);
      } else {
        await api.post<PreguntaFrecuente>('/portal/preguntas-frecuentes', cuerpo);
      }
      alGuardar();
      alCerrar();
      notificar.exito(editando ? 'Pregunta actualizada' : 'Pregunta creada');
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar pregunta' : 'Nueva pregunta frecuente'}
      subtitulo="Se ve en la ventanilla del ciudadano"
      icono={HelpCircle}
      ancho="ancho"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <Campo
          etiqueta="Pregunta"
          requerido
          ayuda="COMO LA HARÍA EL CIUDADANO: «¿DÓNDE PUEDO PAGAR?»"
        >
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className={claseEntrada}
            maxLength={300}
            autoFocus
          />
        </Campo>

        <Campo etiqueta="Respuesta" requerido ayuda="CORTA Y DIRECTA. SE LEE EN EL TELÉFONO.">
          <textarea
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            className={`${claseEntrada} min-h-32 py-2`}
            maxLength={4000}
            rows={6}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Categoría" ayuda="PARA AGRUPARLAS: PAGOS, MI CUENTA… OPCIONAL">
            <input
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className={claseEntrada}
              list="categorias-de-preguntas"
              maxLength={60}
            />
            <datalist id="categorias-de-preguntas">
              {categorias.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Campo>

          <div className="flex items-start gap-3 rounded-xl border border-armazon-200 p-3">
            <span className="mt-0.5 shrink-0">
              <Interruptor
                activo={publicada}
                alCambiar={() => setPublicada((v) => !v)}
                etiqueta="Publicada"
              />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium uppercase text-armazon-900">
                Publicada
              </span>
              <span className="mt-0.5 block text-[11px] uppercase leading-relaxed text-armazon-500">
                Sin publicar se guarda, pero no se ve en la ventanilla.
              </span>
            </span>
          </div>
        </div>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar={editando ? 'Actualizar pregunta' : 'Crear pregunta'}
        />
      </form>
    </Modal>
  );
}
